import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { reportingApi } from '../services/reportingApi';
import { connectorsApi } from '../modules/connectors/services/connectorsApi';

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

const downloadBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  window.URL.revokeObjectURL(url);
};

const getFilenameFromResponse = (response, fallback) => {
  const cd = response.headers?.['content-disposition'] || '';
  const match = cd.match(/filename="?([^"]+)"?/);
  return match ? match[1] : fallback;
};

const readBlobError = async (err) => {
  const blob = err.response?.data;
  if (blob instanceof Blob) {
    try {
      const text = await blob.text();
      const json = JSON.parse(text);
      return json.detail || text;
    } catch {
      return err.message || 'Erreur inconnue';
    }
  }
  return err.response?.data?.detail || err.message || 'Erreur inconnue';
};

const toInputDate = (d) => {
  if (!d) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(d)) return d;
  const parts = d.split('/');
  if (parts.length !== 3) return d;
  const [day, month, year] = parts;
  const fy = year.length === 2 ? (parseInt(year, 10) < 50 ? `20${year}` : `19${year}`) : year;
  return `${fy}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
};

const formatDisplayDate = (d) => {
  if (!d) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(d)) {
    const [y, m, day] = d.split('-');
    return `${day}/${m}/${y}`;
  }
  if (/^\d{2}\/\d{2}\/\d{2}$/.test(d)) {
    const [day, month, year] = d.split('/');
    const fy = parseInt(year, 10) < 50 ? `20${year}` : `19${year}`;
    return `${day}/${month}/${fy}`;
  }
  return d;
};

const StatusBadge = ({ status }) => {
  const cls = {
    completed: 'bg-green-100 text-green-800',
    failed: 'bg-red-100 text-red-800',
    running: 'bg-yellow-100 text-yellow-800',
    precomputing: 'bg-orange-100 text-orange-800',
    pending: 'bg-slate-100 text-slate-600',
  }[status] || 'bg-slate-100 text-slate-600';
  const labels = { completed: 'Terminé', failed: 'Échec', running: 'En cours', precomputing: 'Pré-calcul…', pending: 'En attente' };
  return <span className={`px-2 py-0.5 text-xs font-medium rounded-sm ${cls}`}>{labels[status] || status}</span>;
};

const Alert = ({ type = 'info', children }) => {
  const cls = {
    info: 'bg-blue-50 border-blue-300 text-blue-800',
    success: 'bg-green-50 border-green-300 text-green-800',
    error: 'bg-red-50 border-red-300 text-red-800',
    warning: 'bg-amber-50 border-amber-300 text-amber-800',
  }[type];
  return <div className={`p-4 text-sm border ${cls}`}>{children}</div>;
};

// ─────────────────────────────────────────────────────────────────────────────
// Page: Produire un reporting
// ─────────────────────────────────────────────────────────────────────────────
export const ReportingProducePage = () => {
  const [form, setForm] = useState({ closingDate: '', previousClosingDate: '', fichier: '', skipAutoCorrections: false });
  const [fichiers, setFichiers] = useState([]);
  const [runs, setRuns] = useState([]);
  const [refReady, setRefReady] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [showImport, setShowImport] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [activeRunId, setActiveRunId] = useState(null);
  const [showReimportZip, setShowReimportZip] = useState(false);
  const [reimportZipFile, setReimportZipFile] = useState(null);
  const [reimportZipLoading, setReimportZipLoading] = useState(false);
  const [reimportZipMessage, setReimportZipMessage] = useState(null);
  const [declStatus, setDeclStatus] = useState(null);
  const [validateLoading, setValidateLoading] = useState(false);

  const loadContext = useCallback(async () => {
    try {
      const [countsRes, fichiersRes, runsRes] = await Promise.all([
        reportingApi.getReferenceCounts().catch(() => ({ data: { sm_poste_count: 0 } })),
        reportingApi.getFichiers().catch(() => ({ data: { items: [] } })),
        reportingApi.getRuns(10).catch(() => ({ data: { items: [] } })),
      ]);
      setRefReady((countsRes.data?.sm_poste_count || 0) > 0);
      setFichiers(fichiersRes.data?.items || []);
      setRuns(runsRes.data?.items || []);
    } catch {
      setRefReady(false);
    }
  }, []);

  useEffect(() => { loadContext(); }, [loadContext]);

  // Charger le statut de validation quand closingDate change
  useEffect(() => {
    if (!form.closingDate) { setDeclStatus(null); return; }
    reportingApi.getDeclarationStatus(form.closingDate)
      .then((res) => setDeclStatus(res.data))
      .catch(() => setDeclStatus(null));
  }, [form.closingDate]);

  const handleValidate = async () => {
    if (!form.closingDate) return;
    setValidateLoading(true);
    try {
      const newValidated = !(declStatus?.validated);
      const res = await reportingApi.validateDeclaration(form.closingDate, newValidated);
      setDeclStatus(res.data);
      setMessage({ type: 'success', text: newValidated ? `Déclaration du ${form.closingDate} validée.` : `Validation levée pour le ${form.closingDate}.` });
    } catch (err) {
      setMessage({ type: 'error', text: `Erreur lors de la validation : ${err.response?.data?.detail || err.message}` });
    } finally {
      setValidateLoading(false);
    }
  };

  // Auto-refresh si un run est actif
  useEffect(() => {
    const hasActive = runs.some((r) => r.status === 'running' || r.status === 'pending' || r.status === 'precomputing');
    if (!hasActive) return;
    const timer = setInterval(() => {
      reportingApi.getRuns(10).then((res) => setRuns(res.data?.items || []));
    }, 5000);
    return () => clearInterval(timer);
  }, [runs]);

  const handleProduce = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);
    try {
      const res = await reportingApi.produce({
        closing_date: form.closingDate,
        previous_closing_date: form.previousClosingDate,
        fichier: form.fichier || null,
        skip_auto_corrections: form.skipAutoCorrections || false,
      });
      const run = res.data;
      if (run.status === 'completed') {
        setMessage({ type: 'success', text: `Production terminée — ${run.rows_loaded} postes chargés pour l'arrêté du ${run.closing_date}.` });
        setActiveRunId(null);
      } else if (run.status === 'running' || run.status === 'pending') {
        setMessage({ type: 'info', text: `Production lancée (run #${run.id}). L'import est en cours en arrière-plan. Suivez le statut dans l'historique ci-dessous.` });
        setActiveRunId(run.id);
      } else if (run.status === 'failed') {
        setMessage({ type: 'error', text: `Échec de la production : ${run.error_message || 'erreur inconnue.'}` });
        setActiveRunId(null);
      } else {
        setMessage({ type: 'info', text: `Production lancée (statut: ${run.status || 'inconnu'}).` });
        setActiveRunId(null);
      }
      await loadContext();
    } catch (err) {
      const detail = err.response?.data?.detail || err.response?.data?.message || err.message;
      setMessage({ type: 'error', text: `Erreur lors de la production : ${detail}` });
    } finally {
      setLoading(false);
    }
  };

  const hasActiveRun = runs.some((r) => r.status === 'running' || r.status === 'pending' || r.status === 'precomputing');
  const canSubmit = form.closingDate && form.previousClosingDate && !loading && refReady && !hasActiveRun;

  return (
    <div className="max-w-5xl mx-auto px-6 py-6 space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900 mb-1">Produire un reporting</h1>
        <p className="text-sm text-slate-500">
          Chargement des soldes comptables depuis Amplitude pour une date d&apos;arrêté donnée.
        </p>
      </div>

      {refReady === false && (
        <Alert type="warning">
          Les données de référence COBAC ne sont pas encore initialisées.{' '}
          <a href="/settings" className="underline font-medium">Aller dans les Paramétrages</a> pour les charger avant de lancer une production.
        </Alert>
      )}

      {message && <Alert type={message.type}>{message.text}</Alert>}

      <div className="bg-white border border-slate-200 shadow-sm">
        <div className="px-6 py-4 border-b border-slate-100">
          <h2 className="text-base font-semibold text-slate-800">Paramètres de production</h2>
        </div>
        <form onSubmit={handleProduce} className="p-6 space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <label className="block space-y-1.5">
              <span className="text-sm font-medium text-slate-700">Date d&apos;arrêté à produire <span className="text-red-500">*</span></span>
              <input
                type="date"
                value={form.closingDate}
                onChange={(e) => setForm((p) => ({ ...p, closingDate: e.target.value }))}
                className="w-full border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:border-[#E20613]"
                required
              />
            </label>
            <label className="block space-y-1.5">
              <span className="text-sm font-medium text-slate-700">Date d&apos;arrêté précédent <span className="text-red-500">*</span></span>
              <input
                type="date"
                value={form.previousClosingDate}
                onChange={(e) => setForm((p) => ({ ...p, previousClosingDate: e.target.value }))}
                className="w-full border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:border-[#E20613]"
                required
              />
            </label>
          </div>

          <label className="block space-y-1.5">
            <span className="text-sm font-medium text-slate-700">Périmètre du reporting</span>
            <select
              value={form.fichier}
              onChange={(e) => setForm((p) => ({ ...p, fichier: e.target.value }))}
              className="w-full border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:border-[#E20613]"
            >
              <option value="">Tous les fichiers ({fichiers.length} fichiers COBAC)</option>
              {fichiers.map((f) => (
                <option key={f.fichier} value={f.fichier}>
                  {f.fichier}{f.fichier_label ? ` — ${f.fichier_label}` : ''}{` (${f.declarations.length} déclaration${f.declarations.length > 1 ? 's' : ''})`}
                </option>
              ))}
            </select>
            <p className="text-xs text-slate-400">Laissez vide pour produire l&apos;ensemble des déclarations COBAC.</p>
          </label>

          <label className="flex items-start gap-2.5 pt-1">
            <input
              type="checkbox"
              checked={form.skipAutoCorrections}
              onChange={(e) => setForm((p) => ({ ...p, skipAutoCorrections: e.target.checked }))}
              className="mt-0.5 h-4 w-4 border border-slate-300 rounded focus:ring-[#E20613]"
            />
            <span className="text-sm text-slate-700">
              <span className="font-medium">Sauter les corrections automatiques (CID)</span>
              <span className="block text-xs text-slate-400 mt-0.5">
                Conserve les valeurs brutes Amplitude sans appliquer CQuali / RECF100 / CInter / CCplex / CIntra.
                Les corrections pourront être appliquées manuellement via l&rsquo;UI CID après l&rsquo;import.
              </span>
            </span>
          </label>

          <div className="pt-1 flex flex-wrap gap-3">
            <button
              type="submit"
              disabled={!canSubmit}
              className="px-5 py-2 bg-[#E20613] text-white text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[#c90511] flex items-center gap-2"
            >
              {hasActiveRun && (
                <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              )}
              {hasActiveRun ? 'Import en cours (arrière-plan)…' : loading ? 'Lancement…' : 'Lancer la production'}
            </button>
            <button
              type="button"
              onClick={() => setShowImport((v) => !v)}
              className="px-4 py-2 border border-slate-300 text-slate-600 text-sm hover:bg-slate-50"
            >
              {showImport ? 'Masquer l\'import' : 'Importer depuis Excel (manuels)'}
            </button>
            <button
              type="button"
              onClick={() => { setShowReimportZip((v) => !v); setReimportZipMessage(null); }}
              className="px-4 py-2 border border-blue-600 text-blue-600 text-sm hover:bg-blue-50"
            >
              {showReimportZip ? 'Masquer le réimport' : '↑ Réimporter un lot ZIP'}
            </button>
            {form.closingDate && (
              <button
                type="button"
                onClick={handleValidate}
                disabled={validateLoading}
                className={`px-4 py-2 text-sm font-medium border disabled:opacity-40 ${
                  declStatus?.validated
                    ? 'border-green-600 text-green-700 bg-green-50 hover:bg-green-100'
                    : 'border-amber-500 text-amber-700 bg-amber-50 hover:bg-amber-100'
                }`}
              >
                {validateLoading ? '…' : declStatus?.validated ? '✓ Déclaration validée' : 'Valider la déclaration'}
              </button>
            )}
          </div>
        </form>
      </div>

      {showImport && (
        <div className="bg-white border border-slate-200 shadow-sm">
          <div className="px-6 py-4 border-b border-slate-100">
            <h2 className="text-base font-semibold text-slate-800">Import de déclarations manuelles (Excel)</h2>
            <p className="text-xs text-slate-400 mt-0.5">
              Fichiers concernés : F161X, F162X, F163X, F1117, F140X, F142X, F200X. Chaque onglet du classeur = une déclaration.
            </p>
          </div>
          <div className="p-6 space-y-4">
            {message?.source === 'import' && <Alert type={message.type}>{message.text}</Alert>}
            <label className="block space-y-1.5">
              <span className="text-sm font-medium text-slate-700">Date d&apos;arrêté <span className="text-red-500">*</span></span>
              <input
                type="date"
                value={form.closingDate}
                onChange={(e) => setForm((p) => ({ ...p, closingDate: e.target.value }))}
                className="border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:border-[#E20613]"
              />
            </label>
            <label className="block space-y-1.5">
              <span className="text-sm font-medium text-slate-700">Fichier Excel (.xlsx)</span>
              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                className="block text-sm text-slate-600 file:mr-3 file:py-1.5 file:px-3 file:border file:border-slate-300 file:text-sm file:bg-white file:text-slate-700 hover:file:bg-slate-50"
              />
            </label>
            <button
              type="button"
              disabled={!importFile || !form.closingDate || importLoading}
              onClick={async () => {
                setImportLoading(true);
                setMessage(null);
                try {
                  const res = await reportingApi.uploadManual(importFile, form.closingDate);
                  const d = res.data;
                  setMessage({ source: 'import', type: 'success', text: `${d.message} — fichiers : ${d.fichiers.join(', ')}` });
                  setImportFile(null);
                } catch (err) {
                  const msg = typeof err.response?.data?.detail === 'object'
                    ? err.response.data.detail.message
                    : err.response?.data?.detail || 'Erreur lors de l\'import.';
                  setMessage({ source: 'import', type: 'error', text: msg });
                } finally {
                  setImportLoading(false);
                }
              }}
              className="px-4 py-2 bg-[#E20613] text-white text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[#c90511]"
            >
              {importLoading ? 'Import en cours…' : 'Importer le fichier'}
            </button>
          </div>
        </div>
      )}

      {showReimportZip && (
        <div className="bg-white border border-slate-200 shadow-sm">
          <div className="px-6 py-4 border-b border-slate-100">
            <h2 className="text-base font-semibold text-slate-800">Réimporter un lot ZIP (écrase les valeurs auto)</h2>
            <p className="text-xs text-slate-400 mt-0.5">
              ZIP contenant des fichiers TXT COBAC. Chaque TXT écrase les valeurs auto générées depuis Amplitude pour la date d&apos;arrêté sélectionnée.
            </p>
          </div>
          <div className="p-6 space-y-4">
            {reimportZipMessage && <Alert type={reimportZipMessage.type}>{reimportZipMessage.text}</Alert>}
            <label className="block space-y-1.5">
              <span className="text-sm font-medium text-slate-700">Date d&apos;arrêté <span className="text-red-500">*</span></span>
              <input
                type="date"
                value={form.closingDate}
                onChange={(e) => setForm((p) => ({ ...p, closingDate: e.target.value }))}
                className="border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:border-[#E20613]"
              />
            </label>
            <label className="block space-y-1.5">
              <span className="text-sm font-medium text-slate-700">Fichier ZIP (.zip)</span>
              <input
                type="file"
                accept=".zip"
                onChange={(e) => setReimportZipFile(e.target.files?.[0] || null)}
                className="block text-sm text-slate-600 file:mr-3 file:py-1.5 file:px-3 file:border file:border-slate-300 file:text-sm file:bg-white file:text-slate-700 hover:file:bg-slate-50"
              />
            </label>
            <button
              type="button"
              disabled={!reimportZipFile || !form.closingDate || reimportZipLoading}
              onClick={async () => {
                setReimportZipLoading(true);
                setReimportZipMessage(null);
                try {
                  const res = await reportingApi.importZip(reimportZipFile, form.closingDate);
                  const d = res.data;
                  let text = d.message;
                  if (d.erreurs && d.erreurs.length > 0) {
                    text += `\nErreurs : ${d.erreurs.join(', ')}`;
                  }
                  setReimportZipMessage({ type: 'success', text });
                  setReimportZipFile(null);
                  loadContext();
                } catch (err) {
                  const msg = typeof err.response?.data?.detail === 'object'
                    ? err.response.data.detail.message
                    : err.response?.data?.detail || 'Erreur lors du réimport.';
                  setReimportZipMessage({ type: 'error', text: msg });
                } finally {
                  setReimportZipLoading(false);
                }
              }}
              className="px-4 py-2 bg-blue-600 text-white text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed hover:bg-blue-700"
            >
              {reimportZipLoading ? 'Réimport en cours…' : 'Réimporter le ZIP'}
            </button>
          </div>
        </div>
      )}

      <div className="bg-white border border-slate-200 shadow-sm">
        <div className="px-6 py-4 border-b border-slate-100">
          <h2 className="text-base font-semibold text-slate-800">Historique des productions</h2>
        </div>
        {runs.length === 0 ? (
          <p className="px-6 py-8 text-sm text-slate-400 text-center">Aucune production effectuée pour le moment.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide border-b border-slate-200">
                <tr>
                  <th className="px-5 py-3 text-left font-medium">Date d&apos;arrêté</th>
                  <th className="px-5 py-3 text-left font-medium">Périmètre</th>
                  <th className="px-5 py-3 text-left font-medium">Statut</th>
                  <th className="px-5 py-3 text-left font-medium">Postes chargés</th>
                  <th className="px-5 py-3 text-left font-medium">Lancé par</th>
                  <th className="px-5 py-3 text-left font-medium">Le</th>
                </tr>
              </thead>
              <tbody>
                {runs.map((run) => (
                  <tr key={run.id} className="border-t border-slate-100 hover:bg-slate-50">
                    <td className="px-5 py-3 text-sm font-medium text-slate-800">{formatDisplayDate(run.closing_date)}</td>
                    <td className="px-5 py-3 text-sm text-slate-600">{run.fichier ? run.fichier : 'Tous'}</td>
                    <td className="px-5 py-3 text-sm"><StatusBadge status={run.status} /></td>
                    <td className="px-5 py-3 text-sm text-slate-600">{run.rows_loaded ?? '—'}</td>
                    <td className="px-5 py-3 text-sm text-slate-500">{run.started_by || '—'}</td>
                    <td className="px-5 py-3 text-sm text-slate-400">{run.started_at ? new Date(run.started_at).toLocaleString('fr-FR') : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Modal: Sélection de fichiers pour export ZIP
// ─────────────────────────────────────────────────────────────────────────────
const FileSelectionModal = ({ isOpen, onClose, onConfirm, fichiers, selectedDate, loading }) => {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setSelectedFiles([]);
      setSelectAll(false);
    }
  }, [isOpen]);

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedFiles([]);
    } else {
      setSelectedFiles(fichiers.map(f => f.fichier));
    }
    setSelectAll(!selectAll);
  };

  const handleToggleFile = (fichier) => {
    setSelectedFiles(prev =>
      prev.includes(fichier)
        ? prev.filter(f => f !== fichier)
        : [...prev, fichier]
    );
    setSelectAll(false);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-slate-900">Sélectionner les fichiers à exporter</h3>
            <p className="text-sm text-slate-500 mt-1">
              {selectedDate ? `Date d'arrêté : ${formatDisplayDate(selectedDate)}` : 'Sélectionnez une date'}
            </p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 transition-colors">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {fichiers.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-8">Aucun fichier disponible pour cette date.</p>
          ) : (
            <>
              <div className="mb-4 pb-4 border-b border-slate-200">
                <label className="flex items-center gap-3 cursor-pointer hover:bg-slate-50 p-2 rounded">
                  <input type="checkbox" checked={selectAll} onChange={handleSelectAll}
                    className="w-4 h-4 text-[#E20613] border-slate-300 rounded focus:ring-[#E20613]" />
                  <span className="text-sm font-medium text-slate-700">Tout sélectionner</span>
                  <span className="text-xs text-slate-400 ml-auto">{fichiers.length} fichiers</span>
                </label>
              </div>

              <div className="space-y-1">
                {fichiers.map((f) => (
                  <label key={f.fichier}
                    className={`flex items-center gap-3 p-2 rounded cursor-pointer transition-colors ${
                      selectedFiles.includes(f.fichier) ? 'bg-red-50 hover:bg-red-100' : 'hover:bg-slate-50'
                    }`}>
                    <input type="checkbox" checked={selectedFiles.includes(f.fichier)}
                      onChange={() => handleToggleFile(f.fichier)}
                      className="w-4 h-4 text-[#E20613] border-slate-300 rounded focus:ring-[#E20613]" />
                    <div className="flex-1">
                      <span className="text-sm font-medium text-slate-700 font-mono">{f.fichier}</span>
                      {f.fichier_label && <span className="text-xs text-slate-500 ml-2">— {f.fichier_label}</span>}
                      {f._isManual && (
                        <span className="ml-2 text-[10px] font-semibold uppercase px-1.5 py-0.5 bg-amber-100 text-amber-800 border border-amber-300">M</span>
                      )}
                      {f.declarations && (
                        <span className="text-xs text-slate-400 ml-2">
                          ({f.declarations.length} déclaration{f.declarations.length > 1 ? 's' : ''})
                        </span>
                      )}
                    </div>
                  </label>
                ))}
              </div>
            </>
          )}
        </div>

        <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 flex items-center justify-between rounded-b-lg">
          <div className="text-sm text-slate-600">
            {selectedFiles.length > 0
              ? <span>{selectedFiles.length} fichier{selectedFiles.length > 1 ? 's' : ''} sélectionné{selectedFiles.length > 1 ? 's' : ''}</span>
              : <span className="text-slate-400">Aucun fichier sélectionné</span>}
          </div>
          <div className="flex gap-3">
            <button onClick={onClose}
              className="px-4 py-2 border border-slate-300 text-slate-700 text-sm hover:bg-slate-50 transition-colors">
              Annuler
            </button>
            <button onClick={() => { if (selectedFiles.length === 0) { alert('Veuillez sélectionner au moins un fichier'); return; } onConfirm(selectedFiles); }}
              disabled={selectedFiles.length === 0 || loading}
              className="px-4 py-2 bg-[#E20613] text-white text-sm font-medium hover:bg-[#c90511] disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center gap-2">
              {loading && (
                <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.044 2 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              )}
              {loading ? 'Génération...' : `Exporter ${selectedFiles.length} fichier${selectedFiles.length > 1 ? 's' : ''}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Page: Consulter un reporting
// Navigation: date d'arrêté → fichier → déclaration → tableau des postes
// ─────────────────────────────────────────────────────────────────────────────
export const ReportingConsultPage = () => {
  const [closingDate, setClosingDate] = useState('');
  const [fichiers, setFichiers] = useState([]);
  const [manualFichiers, setManualFichiers] = useState([]);
  const [manualDatesSet, setManualDatesSet] = useState(new Set());
  const [selectedFichier, setSelectedFichier] = useState(null);
  const [selectedDecs, setSelectedDecs] = useState(null);
  const [data, setData] = useState([]);
  const [editedData, setEditedData] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [dataLoading, setDataLoading] = useState(false);
  const [zipLoading, setZipLoading] = useState(false);
  const [txtLoading, setTxtLoading] = useState(false);
  const [xlsLoading, setXlsLoading] = useState(false);
  const [runs, setRuns] = useState([]);
  const [runsLoaded, setRunsLoaded] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showImport, setShowImport] = useState(false);
  const [importFormat, setImportFormat] = useState('txt');
  const [importFile, setImportFile] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [importMessage, setImportMessage] = useState(null);
  const [modifiedFichiersSet, setModifiedFichiersSet] = useState(new Set());
  const [dateFichiersSet, setDateFichiersSet] = useState(null);
  const [showFileModal, setShowFileModal] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [activeFichiersForModal, setActiveFichiersForModal] = useState([]);

  // Set des fichiers ayant une déclaration manuelle pour la date courante
  const manualFichiersSet = useMemo(
    () => new Set(manualFichiers.map((f) => f.fichier)),
    [manualFichiers],
  );

  // Liste fusionnée : catalogue auto + fichiers manuel-seulement.
  // Filtrée par les fichiers ayant réellement des données pour la date sélectionnée.
  const activeFichiers = useMemo(() => {
    if (!closingDate) return [];
    const autoMap = new Map(fichiers.map((f) => [f.fichier, f]));
    const result = [];
    for (const f of fichiers) {
      if (dateFichiersSet && !dateFichiersSet.has(f.fichier)) continue;
      result.push({ ...f, _isManual: manualFichiersSet.has(f.fichier) });
    }
    // Fichiers manuels n'existant pas dans le catalogue auto
    for (const f of manualFichiers) {
      if (autoMap.has(f.fichier)) continue;
      if (dateFichiersSet && !dateFichiersSet.has(f.fichier)) continue;
      result.push({ ...f, _isManual: true });
    }
    return result;
  }, [fichiers, manualFichiers, manualFichiersSet, closingDate, dateFichiersSet]);

  // Liste filtrée par recherche
  const filteredFichiers = useMemo(() => {
    if (!searchQuery) return activeFichiers;
    const q = searchQuery.toLowerCase();
    return activeFichiers
      .map((f) => {
        const fichierMatches = f.fichier.toLowerCase().includes(q) || (f.fichier_label && f.fichier_label.toLowerCase().includes(q));
        const filteredDeclarations = f.declarations.filter((d) =>
          d.decs.toLowerCase().includes(q) || (d.decs_label && d.decs_label.toLowerCase().includes(q))
        );
        if (fichierMatches || filteredDeclarations.length > 0) {
          return { ...f, declarations: fichierMatches ? f.declarations : filteredDeclarations };
        }
        return null;
      })
      .filter((f) => f !== null);
  }, [activeFichiers, searchQuery]);

  // is_manual réel basé sur le fichier sélectionné (et non plus sur la date)
  const isManualSelection = !!selectedFichier?._isManual;

  const loadRuns = useCallback(() => {
    setRunsLoaded(false);
    reportingApi.getFichiers().then((res) => setFichiers(res.data?.items || [])).catch(() => {});
    const runsP = reportingApi.getRuns(50).then((res) => res.data?.items || []).catch(() => []);
    const manualP = reportingApi.getManualDates().then((res) => (res.data?.dates || []).map(toInputDate)).catch(() => []);
    const reimportP = reportingApi.getReimportDates().then((res) => (res.data?.dates || []).map(toInputDate)).catch(() => []);
    Promise.all([runsP, manualP, reimportP]).then(([runItems, manualDates, reimportDates]) => {
      const manSet = new Set(manualDates);
      setManualDatesSet(manSet);
      const reimportSet = new Set(reimportDates);
      const runDates = new Set(runItems.filter((r) => r.status === 'completed').map((r) => toInputDate(r.closing_date)));
      const fakeRuns = [];
      for (const d of manualDates) {
        if (!runDates.has(d)) {
          fakeRuns.push({ id: `manual_${d}`, closing_date: d, status: 'completed', fichier: null, rows_loaded: null, started_by: 'import manuel', started_at: null });
        }
      }
      for (const d of reimportDates) {
        if (!runDates.has(d) && !manSet.has(d)) {
          fakeRuns.push({ id: `reimport_${d}`, closing_date: d, status: 'completed', fichier: null, rows_loaded: null, started_by: 'réimport ZIP', started_at: null });
        }
      }
      const normalRuns = runItems.map((r) => ({ ...r, closing_date: toInputDate(r.closing_date) }));
      setRuns([...normalRuns, ...fakeRuns]);
      setRunsLoaded(true);
    });
  }, []);

  const handleRefresh = useCallback(() => {
    loadRuns();
    if (closingDate) {
      reportingApi.getModifiedFichiers(closingDate).then((res) => {
        setModifiedFichiersSet(new Set(res.data?.fichiers || []));
      }).catch(() => setModifiedFichiersSet(new Set()));
      if (selectedFichier && selectedDecs) {
        setDataLoading(true);
        reportingApi.getData(closingDate, selectedFichier.fichier, selectedDecs.decs, isManualSelection)
          .then((r) => { setData(r.data?.items || []); setEditedData((r.data?.items || []).map((row) => ({ ...row }))); })
          .catch(() => {})
          .finally(() => setDataLoading(false));
      }
    }
  }, [loadRuns, closingDate, selectedFichier, selectedDecs, isManualSelection]);

  useEffect(() => { loadRuns(); }, [loadRuns]);

  // Restore state from sessionStorage (back from CID)
  useEffect(() => {
    try {
      const saved = sessionStorage.getItem('reporting_consult_state');
      if (saved) {
        const s = JSON.parse(saved);
        if (s.closingDate) {
          setClosingDate(s.closingDate);
          setSelectedFichier(s.selectedFichier);
          setSelectedDecs(s.selectedDecs);
        }
        sessionStorage.removeItem('reporting_consult_state');
      }
    } catch {}
  }, []);

  // Pré-chauffe globale CID/CCplex en arrière-plan dès qu'une date est choisie.
  // Idempotent côté serveur ; sans bloquer l'UI.
  useEffect(() => {
    if (!closingDate) return;
    reportingApi.cidWarm(closingDate).catch(() => {});
  }, [closingDate]);

  // Charger les déclarations manuelles existantes pour la date courante.
  // On charge toujours (pas seulement si manualDatesSet contient la date) parce que
  // l'utilisateur peut avoir importé un manuel après le chargement initial des dates.
  useEffect(() => {
    if (!closingDate) { setManualFichiers([]); return; }
    reportingApi.getManualDeclarations(closingDate).then((res) => {
      const decls = res.data?.items || [];
      const map = {};
      for (const d of decls) {
        if (!map[d.fichier]) map[d.fichier] = { fichier: d.fichier, fichier_label: null, declarations: [] };
        map[d.fichier].declarations.push({ decs: d.fichier, decs_label: d.decl_header || d.fichier, nb_postes: 0 });
      }
      setManualFichiers(Object.values(map));
    }).catch(() => setManualFichiers([]));
  }, [closingDate]);

  // Charger les fichiers modifiés (modified=1) et les fichiers ayant des données
  // pour la date courante (pour filtrer le catalogue global)
  useEffect(() => {
    if (!closingDate) {
      setModifiedFichiersSet(new Set());
      setDateFichiersSet(null);
      return;
    }
    reportingApi.getModifiedFichiers(closingDate).then((res) => {
      setModifiedFichiersSet(new Set(res.data?.fichiers || []));
    }).catch(() => setModifiedFichiersSet(new Set()));
    reportingApi.getFichiersForDate(closingDate).then((res) => {
      setDateFichiersSet(new Set(res.data?.fichiers || []));
    }).catch(() => setDateFichiersSet(null));
  }, [closingDate]);

  // Available dates from successful runs (all already YYYY-MM-DD)
  const availableDates = useMemo(() => {
    return [...new Set(runs.filter((r) => r.status === 'completed').map((r) => r.closing_date))].sort().reverse();
  }, [runs]);

  // Load data when fichier+decs+date are all selected
  useEffect(() => {
    if (!closingDate || !selectedFichier || !selectedDecs) { setData([]); setEditedData([]); return; }
    setDataLoading(true);
    setEditMode(false);
    reportingApi.getData(closingDate, selectedFichier.fichier, selectedDecs.decs, isManualSelection)
      .then((res) => {
        const items = res.data?.items || [];
        setData(items);
        setEditedData(items.map((r) => ({ ...r })));
      })
      .catch(() => { setData([]); setEditedData([]); })
      .finally(() => setDataLoading(false));
  }, [closingDate, selectedFichier, selectedDecs, isManualSelection]);

  const handleEditCell = useCallback((rowIdx, colKey, value) => {
    setEditedData((prev) => {
      const next = [...prev];
      next[rowIdx] = { ...next[rowIdx], [colKey]: value === '' ? '' : Number(value) };
      return next;
    });
  }, []);

  const navigate = useNavigate();
  const totalMontant = data.reduce((s, r) => s + (r.montant || 0), 0);

  const handleOpenCID = () => {
    if (!closingDate || !selectedFichier) return;
    const decs = selectedDecs?.decs || '';
    sessionStorage.setItem('reporting_consult_state', JSON.stringify({ closingDate, selectedFichier, selectedDecs }));
    navigate(`/reporting/cid/${closingDate}/${selectedFichier.fichier}?decs=${encodeURIComponent(decs)}`);
  };

  const handleImport = async () => {
    if (!importFile || !closingDate) return;
    setImportLoading(true);
    setImportMessage(null);
    try {
      const decs = selectedDecs?.decs || '';
      let res;
      if (importFormat === 'txt') {
        res = await reportingApi.importTxt(importFile, closingDate, decs);
      } else {
        res = await reportingApi.importExcel(importFile, closingDate);
      }
      const d = res.data;
      setImportMessage({ type: 'success', text: d.message });
      setImportFile(null);
      // Refresh modified fichiers list
      reportingApi.getModifiedFichiers(closingDate).then((r) => {
        setModifiedFichiersSet(new Set(r.data?.fichiers || []));
      }).catch(() => {});
      // Reload data
      if (selectedFichier && selectedDecs) {
        reportingApi.getData(closingDate, selectedFichier.fichier, selectedDecs.decs, isManualSelection)
          .then((r) => { setData(r.data?.items || []); setEditedData((r.data?.items || []).map((row) => ({ ...row }))); })
          .catch(() => {});
      }
    } catch (err) {
      const msg = typeof err.response?.data?.detail === 'object'
        ? err.response.data.detail.message
        : err.response?.data?.detail || err.message || 'Erreur lors du réimport.';
      setImportMessage({ type: 'error', text: msg });
    } finally {
      setImportLoading(false);
    }
  };

  // Fichier liste (F1120, F1121…) : pas de clé code/libelle → affichage dynamique
  const isListFile = useMemo(() => {
    if (!data.length || isManualSelection) return false;
    return !('code' in data[0]) && !('libelle' in data[0]);
  }, [data, isManualSelection]);

  // Colonnes dynamiques pour les fichiers auto (exclure champs non-numériques connus)
  const autoValueCols = useMemo(() => {
    if (!data.length || isManualSelection || isListFile) return [];
    const excluded = new Set(['poste', 'code', 'libelle', 'libelle_poste', 'charef', 'senscompta', 'sens', 'montant']);
    return Object.keys(data[0]).filter((k) => !excluded.has(k));
  }, [data, isManualSelection, isListFile]);

  // Colonnes pour fichiers liste : toutes les clés du premier objet
  const listCols = useMemo(() => {
    if (!isListFile || !data.length) return [];
    return Object.keys(data[0]);
  }, [isListFile, data]);

  const handleExportZip = () => {
    if (!closingDate) return;
    // Le modal doit afficher TOUS les fichiers du catalogue (auto + manuel),
    // pas seulement ceux ayant des données pour la date (activeFichiers est filtré par dateFichiersSet).
    // Le ZIP "tous" génère aussi les header-only sans données.
    const autoMap = new Map(fichiers.map((f) => [f.fichier, f]));
    const allModalFiles = [
      ...fichiers.map(f => ({ ...f, _isManual: manualFichiersSet.has(f.fichier) })),
      ...manualFichiers.filter(f => !autoMap.has(f.fichier)).map(f => ({ ...f, _isManual: true })),
    ];
    if (allModalFiles.length === 0) {
      alert('Aucun fichier disponible dans le catalogue.');
      return;
    }
    setActiveFichiersForModal(allModalFiles);
    setShowFileModal(true);
  };

  const handleExportZipConfirm = async (selectedFiles) => {
    setModalLoading(true);
    setZipLoading(true);
    try {
      const allFiles = activeFichiersForModal.map(f => f.fichier);
      const isAllSelected = selectedFiles.length === allFiles.length;
      let res;
      if (isAllSelected) {
        res = await reportingApi.exportZip(closingDate);
      } else {
        res = await reportingApi.exportZipSelected(closingDate, selectedFiles);
      }
      const filename = isAllSelected
        ? getFilenameFromResponse(res, `reporting-${closingDate}.zip`)
        : getFilenameFromResponse(res, `reporting-${closingDate}-selected.zip`);
      downloadBlob(res.data, filename);
      setShowFileModal(false);
    } catch (err) {
      const msg = await readBlobError(err);
      alert(`Erreur ZIP : ${msg}`);
    } finally {
      setModalLoading(false);
      setZipLoading(false);
    }
  };

  return (
    <div className="flex flex-col h-[calc(100vh-4rem)] overflow-hidden">

      {/* ── Top bar ── */}
      <div className="px-6 py-3 border-b border-slate-200 bg-white flex items-center justify-between shrink-0">
        <div>
          <h1 className="text-base font-semibold text-slate-900">Consulter un reporting</h1>
          <p className="text-xs text-slate-400">Sélectionnez une date d&apos;arrêté puis une déclaration</p>
        </div>
        <div className="flex items-center gap-2">
          {closingDate && (
            <button type="button" disabled={zipLoading}
              onClick={handleExportZip}
              className={`px-3 py-1.5 bg-[#E20613] text-white text-xs font-medium hover:bg-[#c90511] disabled:opacity-60 disabled:cursor-wait`}>
              {zipLoading ? '⏳ Génération…' : '↓ Générer ZIP'}
            </button>
          )}
          <button type="button" onClick={() => handleRefresh()}
            className="px-3 py-1.5 border border-slate-300 text-slate-500 text-xs hover:bg-slate-50">
            ↺ Rafraîchir
          </button>
        </div>
      </div>

      {/* ── Split pane ── */}
      <div className="flex flex-1 min-h-0">

        {/* ── LEFT: Navigation sidebar ── */}
        <div className="w-72 shrink-0 border-r border-slate-200 bg-white flex flex-col min-h-0">

          {/* Date selector */}
          <div className="px-4 py-3 border-b border-slate-100 shrink-0 space-y-2">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Date d&apos;arrêté</p>
            <input type="date" value={closingDate}
              onChange={(e) => { setClosingDate(e.target.value); setSelectedFichier(null); setSelectedDecs(null); setSearchQuery(''); }}
              className="w-full border border-slate-300 px-2 py-1.5 text-sm focus:outline-none focus:border-[#E20613]" />
            {runsLoaded && availableDates.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {availableDates.map((d) => (
                  <button key={d} onClick={() => { setClosingDate(d); setSelectedFichier(null); setSelectedDecs(null); setSearchQuery(''); }}
                    className={`px-2 py-0.5 text-xs border ${closingDate === d ? 'bg-[#E20613] text-white border-[#E20613]' : 'border-slate-300 text-slate-600 hover:bg-slate-50'}`}>
                    {formatDisplayDate(d)}{manualDatesSet.has(d) && <span className="ml-0.5 opacity-60">·M</span>}
                  </button>
                ))}
              </div>
            )}
            {runsLoaded && availableDates.length === 0 && (
              <p className="text-xs text-amber-700">Aucune production.{' '}
                <a href="/reporting/produce" className="underline font-medium">Lancer une production</a>
              </p>
            )}
          </div>

          {/* Search input */}
          {closingDate && (
            <div className="px-4 py-2 border-b border-slate-100 shrink-0">
              <input
                type="text"
                placeholder="Rechercher fichier ou déclaration..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full border border-slate-300 px-2 py-1.5 text-sm focus:outline-none focus:border-[#E20613]"
              />
            </div>
          )}

          {/* Fichiers + declarations tree */}
          <div className="flex-1 overflow-y-auto">
            {!closingDate ? (
              <p className="px-4 py-8 text-xs text-slate-400 text-center">Choisissez une date d&apos;arrêté</p>
            ) : filteredFichiers.length === 0 ? (
              <p className="px-4 py-6 text-xs text-slate-400">Aucun résultat pour cette recherche.</p>
            ) : (
              filteredFichiers.map((f) => (
                <div key={f.fichier}>
                  <div className="px-4 py-2 bg-slate-50 border-b border-t border-slate-100 flex items-center gap-2 sticky top-0 z-[1]">
                    <span className="text-xs font-bold text-slate-700 font-mono">{f.fichier}</span>
                    {f._isManual && (
                      <span className="text-[10px] font-semibold uppercase px-1.5 py-0.5 bg-amber-100 text-amber-800 border border-amber-300" title="Déclaration manuelle importée (Excel)">M</span>
                    )}
                    {modifiedFichiersSet.has(f.fichier) && (
                      <span className="text-[10px] font-semibold uppercase px-1.5 py-0.5 bg-blue-100 text-blue-800 border border-blue-300" title="Valeurs auto écrasées par réimport">Mod</span>
                    )}
                    {f.fichier_label && <span className="text-xs text-slate-500 truncate">{f.fichier_label}</span>}
                    <span className="ml-auto text-xs text-slate-400 shrink-0">{f.declarations.length}</span>
                  </div>
                  {f.declarations.map((d) => {
                    const isActive = selectedDecs?.decs === d.decs && selectedFichier?.fichier === f.fichier;
                    return (
                      <button key={d.decs}
                        onClick={() => { setSelectedFichier(f); setSelectedDecs(d); }}
                        className={`w-full px-4 py-2.5 text-left border-b border-slate-50 transition-colors ${isActive ? 'bg-[#E20613] text-white' : 'hover:bg-red-50 text-slate-700'}`}>
                        <div className={`text-xs font-mono font-semibold ${isActive ? 'text-red-200' : 'text-slate-400'}`}>{d.decs}</div>
                        <div className={`text-xs truncate mt-0.5 ${isActive ? 'text-red-100' : 'text-slate-600'}`}>{d.decs_label || '—'}</div>
                      </button>
                    );
                  })}
                </div>
              ))
            )}
          </div>
        </div>

        {/* ── RIGHT: Data panel ── */}
        <div className="flex-1 min-w-0 flex flex-col min-h-0 bg-white">
          {!selectedDecs ? (
            <div className="flex-1 flex flex-col items-center justify-center text-slate-400 gap-3">
              <svg xmlns="http://www.w3.org/2000/svg" className="w-10 h-10 text-slate-200" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <p className="text-sm">Sélectionnez une déclaration dans le panneau de gauche</p>
            </div>
          ) : (
            <>
              {/* Panel header */}
              <div className="px-6 py-4 border-b border-slate-200 bg-white flex items-center justify-between shrink-0 flex-wrap gap-2">
                <div>
                  <h2 className="text-base font-semibold text-slate-800">{selectedDecs.decs_label || selectedDecs.decs}</h2>
                  <p className="text-xs text-slate-400">{selectedFichier?.fichier} · {selectedDecs.decs} · arrêté au {formatDisplayDate(closingDate)}
                    {editMode && <span className="ml-2 text-amber-600 font-semibold">— Mode édition</span>}
                  </p>
                </div>
                <div className="flex gap-2 flex-wrap">
                  {!editMode ? (
                    <>
                      <button type="button" onClick={handleOpenCID}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-[#004A99] text-[#004A99] text-xs font-medium hover:bg-red-50">
                        🛡 CID
                      </button>
                      {!isManualSelection && !isListFile && (
                        <button type="button" onClick={() => setEditMode(true)}
                          className="px-3 py-1.5 border border-amber-500 text-amber-700 text-xs font-medium hover:bg-amber-50">
                          ✏ Modifier les montants
                        </button>
                      )}
                      <button type="button" disabled={txtLoading}
                        onClick={async () => {
                          setTxtLoading(true);
                          try {
                            const res = await reportingApi.exportTxt(closingDate, selectedFichier.fichier, selectedDecs.decs, isManualSelection);
                            downloadBlob(res.data, getFilenameFromResponse(res, `${selectedFichier.fichier}-${closingDate}.txt`));
                          } catch (err) {
                            const msg = await readBlobError(err);
                            alert(`Erreur TXT : ${msg}`);
                          } finally { setTxtLoading(false); }
                        }}
                        className="px-3 py-1.5 bg-[#E20613] text-white text-xs hover:bg-[#c90511] disabled:opacity-60 disabled:cursor-wait">
                        {txtLoading ? '⏳…' : '↓ TXT'}
                      </button>
                      <button type="button" disabled={xlsLoading}
                        onClick={async () => {
                          setXlsLoading(true);
                          try {
                            const res = await reportingApi.exportExcel(closingDate, selectedFichier.fichier, selectedDecs.decs, isManualSelection);
                            downloadBlob(res.data, getFilenameFromResponse(res, `${selectedFichier.fichier}-${closingDate}.xlsx`));
                          } catch (err) {
                            const msg = await readBlobError(err);
                            alert(`Erreur Excel : ${msg}`);
                          } finally { setXlsLoading(false); }
                        }}
                        className="px-3 py-1.5 border border-green-700 text-green-700 text-xs hover:bg-green-50 disabled:opacity-60 disabled:cursor-wait">
                        {xlsLoading ? '⏳…' : '↓ Excel'}
                      </button>
                      <button type="button"
                        onClick={() => { setShowImport((v) => !v); setImportMessage(null); }}
                        className="px-3 py-1.5 border border-blue-600 text-blue-600 text-xs font-medium hover:bg-blue-50">
                        {showImport ? '✕ Annuler import' : '↑ Réimporter'}
                      </button>
                    </>
                  ) : (
                    <>
                      <button type="button" disabled={txtLoading}
                        onClick={async () => {
                          setTxtLoading(true);
                          try {
                            const res = await reportingApi.exportTxtPost({
                              closing_date: closingDate,
                              fichier: selectedFichier.fichier,
                              decs: selectedDecs.decs,
                              rows: editedData,
                            });
                            downloadBlob(res.data, getFilenameFromResponse(res, `${selectedFichier.fichier}-${closingDate}.txt`));
                          } catch (err) {
                            const msg = await readBlobError(err);
                            alert(`Erreur TXT : ${msg}`);
                          } finally { setTxtLoading(false); }
                        }}
                        className="px-3 py-1.5 bg-[#E20613] text-white text-xs hover:bg-[#c90511] disabled:opacity-60 disabled:cursor-wait">
                        {txtLoading ? '⏳…' : '↓ TXT (avec modifs)'}
                      </button>
                      <button type="button" disabled={xlsLoading}
                        onClick={async () => {
                          setXlsLoading(true);
                          try {
                            const res = await reportingApi.exportExcelPost({
                              closing_date: closingDate,
                              fichier: selectedFichier.fichier,
                              decs: selectedDecs.decs,
                              rows: editedData,
                            });
                            downloadBlob(res.data, getFilenameFromResponse(res, `${selectedFichier.fichier}-${closingDate}.xlsx`));
                          } catch (err) {
                            const msg = await readBlobError(err);
                            alert(`Erreur Excel : ${msg}`);
                          } finally { setXlsLoading(false); }
                        }}
                        className="px-3 py-1.5 border border-green-700 text-green-700 text-xs hover:bg-green-50 disabled:opacity-60 disabled:cursor-wait">
                        {xlsLoading ? '⏳…' : '↓ Excel (avec modifs)'}
                      </button>
                      <button type="button"
                        onClick={() => { setEditedData(data.map((r) => ({ ...r }))); setEditMode(false); }}
                        className="px-3 py-1.5 border border-slate-300 text-slate-600 text-xs hover:bg-slate-50">
                        Annuler
                      </button>
                    </>
                  )}
                </div>
              </div>

              {/* Reimport panel */}
              {showImport && !editMode && (
                <div className="px-6 py-4 border-b border-slate-200 bg-blue-50/50 space-y-3">
                  <div className="flex items-center gap-4">
                    <span className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Réimporter (écrase les valeurs auto)</span>
                    <label className="flex items-center gap-1.5 text-xs text-slate-600">
                      <input type="radio" checked={importFormat === 'txt'} onChange={() => setImportFormat('txt')} />
                      TXT
                    </label>
                    <label className="flex items-center gap-1.5 text-xs text-slate-600">
                      <input type="radio" checked={importFormat === 'excel'} onChange={() => setImportFormat('excel')} />
                      Excel
                    </label>
                  </div>
                  {importMessage && <Alert type={importMessage.type}>{importMessage.text}</Alert>}
                  <div className="flex items-center gap-3">
                    <input
                      type="file"
                      accept={importFormat === 'txt' ? '.txt' : '.xlsx,.xls'}
                      onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                      className="block text-sm text-slate-600 file:mr-3 file:py-1.5 file:px-3 file:border file:border-slate-300 file:text-sm file:bg-white file:text-slate-700 hover:file:bg-slate-50"
                    />
                    <button type="button"
                      disabled={!importFile || importLoading}
                      onClick={handleImport}
                      className="px-4 py-1.5 bg-blue-600 text-white text-xs font-medium disabled:opacity-40 disabled:cursor-not-allowed hover:bg-blue-700">
                      {importLoading ? 'Import en cours…' : 'Réimporter'}
                    </button>
                  </div>
                  <p className="text-xs text-slate-400">
                    {importFormat === 'txt'
                      ? 'Le fichier TXT doit avoir le format COBAC (header + lignes poste;valeurs). Les valeurs sont en millions.'
                      : 'Le fichier Excel doit avoir le format exporté COBAC (un onglet par déclaration). Chaque onglet sera réimporté.'}
                  </p>
                </div>
              )}

              {/* Table area */}
              <div className="flex-1 overflow-auto min-h-0">
                {dataLoading ? (
                  <p className="px-6 py-8 text-sm text-slate-400 text-center">Chargement des données…</p>
                ) : data.length === 0 ? (
                  <p className="px-6 py-8 text-sm text-amber-700 text-center bg-amber-50">
                    Aucune donnée pour cet arrêté / cette déclaration.{' '}
                    <a href="/reporting/produce" className="underline font-medium">Lancer la production</a> d&apos;abord.
                  </p>
                ) : isListFile ? (
                  <table className="w-full">
                    <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide border-b border-slate-200 sticky top-0">
                      <tr>
                        {listCols.map((col) => <th key={col} className="px-5 py-3 text-left font-medium">{col.replace(/_/g, ' ')}</th>)}
                      </tr>
                    </thead>
                    <tbody>
                      {data.map((row, i) => (
                        <tr key={i} className="border-t border-slate-100 hover:bg-slate-50">
                          {listCols.map((col) => {
                            const v = row[col];
                            return (
                              <td key={col} className={`px-5 py-3 text-sm ${typeof v === 'number' ? 'text-right font-mono text-slate-800' : 'text-slate-700'}`}>
                            {typeof v === 'number' ? Math.abs(v).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : (v != null && v !== '' ? String(v) : '—')}
                          </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : isManualSelection ? (() => {
                  const valueCols = Object.keys(data[0]).filter((k) => k.startsWith('col_')).sort((a, b) => parseInt(a.slice(4)) - parseInt(b.slice(4)));
                  const hasLibelle = data.some((r) => r.libelle_poste);
                  return (
                    <table className="w-full">
                      <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide border-b border-slate-200 sticky top-0">
                        <tr>
                          <th className="px-5 py-3 text-left font-medium">Code / Champ 1</th>
                          {hasLibelle && <th className="px-5 py-3 text-left font-medium">Libellé</th>}
                          {valueCols.map((c, i) => <th key={c} className="px-5 py-3 text-left font-medium">Colonne {i + 2}</th>)}
                        </tr>
                      </thead>
                      <tbody>
                        {data.map((row, i) => (
                          <tr key={i} className="border-t border-slate-100 hover:bg-slate-50">
                            <td className="px-5 py-3 text-sm font-mono font-semibold text-slate-700">{row.code || '—'}</td>
                            {hasLibelle && <td className="px-5 py-3 text-sm text-slate-500 italic">{row.libelle_poste || <span className="text-amber-600 text-xs">non reconnu</span>}</td>}
                            {valueCols.map((c) => <td key={c} className="px-5 py-3 text-sm text-slate-700">{row[c] != null && row[c] !== '' ? row[c] : '—'}</td>)}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  );
                })() : (
                  <table className="w-full">
                    <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide border-b border-slate-200 sticky top-0">
                      <tr>
                        <th className="px-5 py-3 text-left font-medium">Poste</th>
                        <th className="px-5 py-3 text-left font-medium">Libellé</th>
                        {autoValueCols.length > 0
                          ? autoValueCols.map((col) => <th key={col} className="px-5 py-3 text-right font-medium">{col.replace(/_/g, ' ')}</th>)
                          : <><th className="px-5 py-3 text-left font-medium">Réf. comptable</th><th className="px-5 py-3 text-right font-medium">Montant (XAF)</th></>}
                      </tr>
                    </thead>
                    <tbody>
                      {(editMode ? editedData : data).map((row, i) => (
                        <tr key={i} className={`border-t border-slate-100 ${editMode ? 'bg-amber-50/30' : 'hover:bg-slate-50'}`}>
                          <td className="px-5 py-3 text-sm font-mono font-semibold text-slate-700">{row.poste || row.code || '—'}</td>
                          <td className="px-5 py-3 text-sm text-slate-700 italic">{row.libelle_poste || row.libelle || '—'}</td>
                          {autoValueCols.length > 0
                            ? autoValueCols.map((col) => (
                                <td key={col} className={`px-2 py-1.5 text-sm font-mono text-slate-800 ${typeof (editMode ? editedData[i]?.[col] : row[col]) === 'string' ? 'text-left' : 'text-right'}`}>
                                  {editMode ? (
                                    <input
                                      type="number"
                                      step="any"
                                      value={row[col] ?? ''}
                                      onChange={(e) => handleEditCell(i, col, e.target.value)}
                                      className="w-full text-right border border-amber-300 bg-white px-2 py-1 text-sm font-mono focus:outline-none focus:border-amber-500"
                                    />
                                  ) : (
                                    row[col] != null
                                      ? (typeof row[col] === 'string'
                                        ? row[col]
                                        : Math.abs(Number(row[col])).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }))
                                      : '—'
                                  )}
                                </td>
                              ))
                            : <><td className="px-5 py-3 text-sm text-slate-500">{row.charef || '—'}</td>
                                <td className="px-2 py-1.5 text-sm text-right font-mono text-slate-800">
                                  {editMode ? (
                                    <input type="number" step="any" value={row.montant ?? ''}
                                      onChange={(e) => handleEditCell(i, 'montant', e.target.value)}
                                      className="w-full text-right border border-amber-300 bg-white px-2 py-1 text-sm font-mono focus:outline-none focus:border-amber-500" />
                                  ) : Math.abs(row.montant || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </td></>}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              {/* Footer summary */}
              {!dataLoading && data.length > 0 && (
                <div className="px-6 py-2.5 border-t border-slate-100 bg-slate-50 shrink-0 flex items-center justify-between text-xs text-slate-500">
                  <span>{data.length} {isListFile ? 'lignes' : 'postes'}
                    {editMode && <span className="ml-2 text-amber-600">— Modifiez les cellules puis exportez en TXT ou Excel</span>}
                  </span>
                  {!isManualSelection && autoValueCols.length === 0 && (
                    <span className="font-mono font-semibold text-slate-700">
                      Total : {totalMontant.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} XAF
                    </span>
                  )}
                </div>
              )}
            </>
          )}
        </div>

      </div>

      <FileSelectionModal
        isOpen={showFileModal}
        onClose={() => setShowFileModal(false)}
        onConfirm={handleExportZipConfirm}
        fichiers={activeFichiersForModal}
        selectedDate={closingDate}
        loading={modalLoading}
      />
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Page: Paramétrages
// ─────────────────────────────────────────────────────────────────────────────
export const SettingsPage = () => {
  const [connectors, setConnectors] = useState([]);
  const [currentConnectorId, setCurrentConnectorId] = useState('');
  const [savedConnectorId, setSavedConnectorId] = useState(null);
  const [bankSettings, setBankSettings] = useState({ code_banque: '', pays_iso: 'CMR', decl_type: 'P' });
  const [refCounts, setRefCounts] = useState(null);
  const [savingConnector, setSavingConnector] = useState(false);
  const [seedLoading, setSeedLoading] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    Promise.all([
      connectorsApi.listConfigured({ type: 'oracle' }).catch(() => ({ data: { items: [] } })),
      reportingApi.getSettings().catch(() => ({ data: {} })),
      reportingApi.getReferenceCounts().catch(() => ({ data: { sm_poste_count: 0, sm_label_count: 0, sm_ficposte_count: 0 } })),
    ]).then(([connRes, settingsRes, countsRes]) => {
      const allConnectors = connRes.data?.items || connRes.data || [];
      setConnectors(Array.isArray(allConnectors) ? allConnectors : []);
      const s = settingsRes.data || {};
      setSavedConnectorId(s.db_connector_id);
      setCurrentConnectorId(s.db_connector_id ? String(s.db_connector_id) : '');
      setBankSettings({
        code_banque: s.code_banque || '',
        pays_iso: s.pays_iso || 'CMR',
        decl_type: s.decl_type || 'P',
      });
      setRefCounts(countsRes.data);
    });
  }, []);

  const handleSaveConnector = async () => {
    setSavingConnector(true);
    setMessage(null);
    try {
      await reportingApi.updateSettings({
        db_connector_id: currentConnectorId ? parseInt(currentConnectorId) : null,
        code_banque: bankSettings.code_banque || null,
        pays_iso: bankSettings.pays_iso || null,
        decl_type: bankSettings.decl_type || null,
      });
      setSavedConnectorId(currentConnectorId ? parseInt(currentConnectorId) : null);
      setMessage({ type: 'success', text: 'Paramètres enregistrés.' });
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Erreur lors de la sauvegarde.' });
    } finally {
      setSavingConnector(false);
    }
  };

  const handleSeedTables = async () => {
    setSeedLoading(true);
    setMessage(null);
    try {
      const res = await reportingApi.initTables();
      setRefCounts({ sm_poste_count: res.data.sm_poste_count, sm_label_count: res.data.sm_label_count, sm_ficposte_count: res.data.sm_ficposte_count });
      setMessage({ type: 'success', text: `Tables COBAC chargées : ${res.data.sm_poste_count} postes, ${res.data.sm_label_count} libellés, ${res.data.sm_ficposte_count} mappings fichier-poste.` });
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Erreur lors de l\'initialisation des tables.' });
    } finally {
      setSeedLoading(false);
    }
  };

  const tablesReady = refCounts && refCounts.sm_poste_count > 0;

  return (
    <div className="max-w-3xl mx-auto px-6 py-6 space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900 mb-1">Paramétrages</h1>
        <p className="text-sm text-slate-500">Configuration de la connexion à Amplitude et initialisation des données de référence COBAC.</p>
      </div>

      {message && <Alert type={message.type}>{message.text}</Alert>}

      {/* Section 1 — Connexion Amplitude */}
      <div className="bg-white border border-slate-200 shadow-sm">
        <div className="px-6 py-4 border-b border-slate-100">
          <h2 className="text-base font-semibold text-slate-800">Connexion à Amplitude (Oracle)</h2>
          <p className="text-xs text-slate-400 mt-0.5">
            Sélectionnez le connecteur Oracle configuré pour la base Amplitude. Les données comptables seront lues depuis cette source lors de chaque production.
          </p>
        </div>
        <div className="p-6 space-y-4">
          {connectors.length === 0 ? (
            <Alert type="warning">
              Aucun connecteur Oracle disponible.{' '}
              Créez-en un d&apos;abord dans le module <strong>Connecteurs</strong>, puis revenez ici pour le sélectionner.
            </Alert>
          ) : (
            <label className="block space-y-1.5">
              <span className="text-sm font-medium text-slate-700">Connecteur Oracle pour Amplitude</span>
              <select
                value={currentConnectorId}
                onChange={(e) => setCurrentConnectorId(e.target.value)}
                className="w-full border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:border-[#E20613]"
              >
                <option value="">— Non configuré —</option>
                {connectors.map((c) => (
                  <option key={c.id} value={String(c.id)}>
                    {c.name} {c.app_id ? `(${c.app_id})` : ''} {!c.enabled ? '[désactivé]' : ''}
                  </option>
                ))}
              </select>
            </label>
          )}

          {savedConnectorId && (
            <p className="text-xs text-green-700 bg-green-50 border border-green-200 px-3 py-2">
              ✓ Connecteur actif — ID {savedConnectorId}
            </p>
          )}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-2">
            <label className="block space-y-1.5">
              <span className="text-sm font-medium text-slate-700">Code banque COBAC</span>
              <input
                type="text"
                value={bankSettings.code_banque}
                onChange={(e) => setBankSettings((p) => ({ ...p, code_banque: e.target.value }))}
                placeholder="ex : 10001"
                className="w-full border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:border-[#E20613]"
              />
            </label>
            <label className="block space-y-1.5">
              <span className="text-sm font-medium text-slate-700">Code ISO pays</span>
              <input
                type="text"
                maxLength={3}
                value={bankSettings.pays_iso}
                onChange={(e) => setBankSettings((p) => ({ ...p, pays_iso: e.target.value.toUpperCase() }))}
                placeholder="CMR"
                className="w-full border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:border-[#E20613]"
              />
            </label>
            <label className="block space-y-1.5">
              <span className="text-sm font-medium text-slate-700">Type de déclaration</span>
              <select
                value={bankSettings.decl_type}
                onChange={(e) => setBankSettings((p) => ({ ...p, decl_type: e.target.value }))}
                className="w-full border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:border-[#E20613]"
              >
                <option value="P">P pour provisoire</option>
                <option value="D">D pour définitif</option>
              </select>
            </label>
          </div>

          <button
            onClick={handleSaveConnector}
            disabled={savingConnector || connectors.length === 0}
            className="px-4 py-2 bg-[#E20613] text-white text-sm font-medium disabled:opacity-40 hover:bg-[#c90511]"
          >
            {savingConnector ? 'Enregistrement…' : 'Enregistrer les paramètres'}
          </button>
        </div>
      </div>

      {/* Section 2 — Tables de référence COBAC */}
      <div className="bg-white border border-slate-200 shadow-sm">
        <div className="px-6 py-4 border-b border-slate-100">
          <h2 className="text-base font-semibold text-slate-800">Données de référence COBAC</h2>
          <p className="text-xs text-slate-400 mt-0.5">
            Chargement des tables de référence COBAC (postes, libellés, structure des fichiers) depuis les scripts SQL embarqués.
            À exécuter une fois avant la première production.
          </p>
        </div>
        <div className="p-6 space-y-4">
          {refCounts && (
            <div className="grid grid-cols-3 gap-3 text-center">
              {[
                { label: 'Postes COBAC', count: refCounts.sm_poste_count, desc: 'SM_POSTE' },
                { label: 'Libellés', count: refCounts.sm_label_count, desc: 'SM_LABEL' },
                { label: 'Mappings fichier-poste', count: refCounts.sm_ficposte_count, desc: 'SM_FICPOSTE' },
              ].map((item) => (
                <div key={item.desc} className={`p-3 border ${item.count > 0 ? 'bg-green-50 border-green-200' : 'bg-slate-50 border-slate-200'}`}>
                  <div className={`text-xl font-bold ${item.count > 0 ? 'text-green-800' : 'text-slate-400'}`}>{item.count.toLocaleString()}</div>
                  <div className="text-xs text-slate-600 font-medium">{item.label}</div>
                  <div className="text-xs text-slate-400">{item.desc}</div>
                </div>
              ))}
            </div>
          )}

          <button
            onClick={handleSeedTables}
            disabled={seedLoading}
            className="px-4 py-2 bg-slate-700 text-white text-sm font-medium disabled:opacity-40 hover:bg-slate-600"
          >
            {seedLoading ? 'Chargement en cours…' : tablesReady ? 'Recharger les tables de référence' : 'Charger les tables de référence COBAC'}
          </button>
          {tablesReady && (
            <p className="text-xs text-slate-400">Les tables sont déjà initialisées. Vous pouvez les recharger si les scripts SQL ont été mis à jour.</p>
          )}
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Page: Gestion des utilisateurs
// ─────────────────────────────────────────────────────────────────────────────
export const UserManagementPage = () => {
  const roles = [
    { name: 'Administrateur', desc: 'Accès complet — paramétrages, production, consultation, archivage, gestion des utilisateurs.' },
    { name: 'Contrôleur', desc: 'Peut produire un reporting, consulter, modifier et valider. Ne peut pas gérer les utilisateurs.' },
    { name: 'Lecteur', desc: 'Consultation uniquement. Ne peut ni produire ni modifier.' },
  ];
  return (
    <div className="max-w-3xl mx-auto px-6 py-6 space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900 mb-1">Gestion des utilisateurs</h1>
        <p className="text-sm text-slate-500">Créer des utilisateurs, leur assigner un rôle et gérer leurs accès à l&apos;application.</p>
      </div>
      <div className="bg-white border border-slate-200 shadow-sm">
        <div className="px-6 py-4 border-b border-slate-100">
          <h2 className="text-base font-semibold text-slate-800">Rôles disponibles</h2>
        </div>
        <div className="divide-y divide-slate-100">
          {roles.map((r) => (
            <div key={r.name} className="px-6 py-4 flex items-start justify-between gap-4">
              <div>
                <p className="text-sm font-semibold text-slate-800">{r.name}</p>
                <p className="text-xs text-slate-500 mt-0.5">{r.desc}</p>
              </div>
              <button className="text-xs text-blue-800 border border-blue-200 px-3 py-1 hover:bg-blue-50 shrink-0">Gérer</button>
            </div>
          ))}
        </div>
      </div>
      <div className="bg-white border border-slate-200 shadow-sm p-6">
        <p className="text-sm text-slate-500 text-center">La gestion complète des utilisateurs sera connectée à Keycloak. Cette vue est une prévisualisation.</p>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Page: Archivages
// ─────────────────────────────────────────────────────────────────────────────
export const ArchivesPage = () => {
  const [fichiers, setFichiers] = useState([]);
  const [form, setForm] = useState({ date: '', fichier: '' });
  const [purging, setPurging] = useState(false);
  const [purgeMsg, setPurgeMsg] = useState(null);

  // Charger le catalogue de fichiers (référentiel COBAC)
  useEffect(() => {
    reportingApi.getFichiers().then((res) => {
      const items = res.data?.items || [];
      setFichiers(items);
    }).catch(() => {});
  }, []);

  // Quand la date change, charger les fichiers ayant des données pour cette date
  // et fusionner avec le catalogue pour avoir une liste complète
  useEffect(() => {
    if (!form.date) return;
    reportingApi.getFichiersForDate(form.date).then((res) => {
      const dateFichiers = res.data?.fichiers || [];
      setFichiers((prev) => {
        const existing = new Set(prev.map((f) => f.fichier));
        // Ajouter les fichiers de la date qui ne sont pas dans le catalogue
        const merged = [...prev];
        for (const code of dateFichiers) {
          if (!existing.has(code)) {
            merged.push({ fichier: code, fichier_label: null, declarations: [] });
          }
        }
        return merged;
      });
    }).catch(() => {});
  }, [form.date]);

  const handlePurgeAll = async () => {
    const isTargeted = !!form.fichier;
    const confirmMsg = isTargeted
      ? `Supprimer les données du fichier ${form.fichier} pour l'arrêté du ${form.date} ?\n` +
        `(historique de runs + déclarations manuelles + valeurs calculées)\n` +
        `Le cache comptable sera préservé.\n\n` +
        `Cette action est IRRÉVERSIBLE.`
      : 'Supprimer définitivement toutes les données de cet arrêté ?\n' +
        '(cache comptable + historique de runs + déclarations manuelles)\n\n' +
        'Cette action est IRRÉVERSIBLE.';
    if (!window.confirm(confirmMsg)) return;
    setPurging(true);
    setPurgeMsg(null);
    try {
      const res = await reportingApi.purgeAll(form.date || null, form.fichier || null);
      const d = res.data;
      setPurgeMsg({
        type: 'success',
        text: d.message,
      });
    } catch (err) {
      setPurgeMsg({ type: 'error', text: err.response?.data?.detail || 'Erreur lors de la purge.' });
    } finally {
      setPurging(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-6 py-6 space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900 mb-1">Archivages</h1>
        <p className="text-sm text-slate-500">
          Exporter les données d&apos;un arrêté en SQL, Excel ou TXT, puis les supprimer de la base de production.
        </p>
      </div>

      {purgeMsg && <Alert type={purgeMsg.type}>{purgeMsg.text}</Alert>}

      <div className="bg-white border border-slate-200 shadow-sm">
        <div className="px-6 py-4 border-b border-slate-100">
          <h2 className="text-base font-semibold text-slate-800">Sélection de la période à archiver</h2>
        </div>
        <div className="p-6 space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <label className="block space-y-1.5">
              <span className="text-sm font-medium text-slate-700">Date d&apos;arrêté <span className="text-slate-400 font-normal">(vide = toutes les dates)</span></span>
              <input
                type="date"
                value={form.date}
                onChange={(e) => setForm((p) => ({ ...p, date: e.target.value }))}
                className="w-full border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:border-[#E20613]"
              />
            </label>
            <label className="block space-y-1.5">
              <span className="text-sm font-medium text-slate-700">Fichier de reporting</span>
              <select
                value={form.fichier}
                onChange={(e) => setForm((p) => ({ ...p, fichier: e.target.value }))}
                className="w-full border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:border-[#E20613]"
              >
                <option value="">Tous les fichiers</option>
                {fichiers.map((f) => (
                  <option key={f.fichier} value={f.fichier}>
                    {f.fichier}{f.fichier_label ? ` — ${f.fichier_label}` : ''}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className="flex flex-wrap gap-3 pt-2">
            <button disabled={!form.date} className="px-4 py-2 border border-slate-300 text-slate-700 text-sm hover:bg-slate-50 disabled:opacity-40">Exporter SQL</button>
            <button disabled={!form.date} className="px-4 py-2 border border-slate-300 text-slate-700 text-sm hover:bg-slate-50 disabled:opacity-40">Exporter Excel</button>
            <button disabled={!form.date} className="px-4 py-2 border border-slate-300 text-slate-700 text-sm hover:bg-slate-50 disabled:opacity-40">Exporter TXT</button>
          </div>

          <div className="pt-2 border-t border-slate-100 space-y-2">
            <button
              disabled={purging}
              className="px-4 py-2 bg-red-700 text-white text-sm font-medium hover:bg-red-800 disabled:opacity-40"
              onClick={handlePurgeAll}
            >
              {purging ? 'Suppression en cours…' : form.date && form.fichier ? `Supprimer ${form.fichier} du ${form.date}` : form.date ? `Supprimer toutes les données du ${form.date}` : 'Supprimer TOUTES les données'}
            </button>
            <p className="text-xs text-slate-400">
              {form.fichier
                ? `Supprime les runs, déclarations manuelles et valeurs calculées pour le fichier ${form.fichier} uniquement. Le cache comptable est préservé.`
                : "Supprime le cache comptable, l'historique des productions et les déclarations manuelles."}
              {' '}Les données disparaîtront immédiatement de l&apos;écran de consultation.
              Cette action est irréversible.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Page cachée : Import manuel Amplitude via CSV SQL Developer
// URL : /reporting/import-manuel  (non référencée dans la navigation)
// ─────────────────────────────────────────────────────────────────────────────
export const ReportingManualImportPage = () => {
  const [form, setForm] = useState({ closingDate: '', previousClosingDate: '' });
  const [csvFile, setCsvFile] = useState(null);
  const [transfertFile, setTransfertFile] = useState(null);
  const [bktauFile, setBktauFile] = useState(null);
  const [message, setMessage] = useState(null);
  const [transfertMessage, setTransfertMessage] = useState(null);
  const [bktauMessage, setBktauMessage] = useState(null);
  const [dlLoading, setDlLoading] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [transfertLoading, setTransfertLoading] = useState(false);
  const [bktauLoading, setBktauLoading] = useState(false);
  const [run, setRun] = useState(null);
  const [runs, setRuns] = useState([]);

  useEffect(() => {
    reportingApi.getRuns(10).then((res) => setRuns(res.data?.items || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!run) return;
    if (run.status === 'completed' || run.status === 'failed') return; // precomputing → keep polling
    const timer = setInterval(() => {
      reportingApi.getRuns(10).then((res) => {
        const items = res.data?.items || [];
        setRuns(items);
        const updated = items.find((r) => r.id === run.id);
        if (updated) setRun(updated);
      }).catch(() => {});
    }, 4000);
    return () => clearInterval(timer);
  }, [run]);

  const canDownload = form.closingDate && form.previousClosingDate;
  const canImport = canDownload && csvFile;

  const handleDownloadSql = async () => {
    setDlLoading(true);
    setMessage(null);
    try {
      const res = await reportingApi.downloadSql(form.closingDate, form.previousClosingDate);
      const cd = form.closingDate.replace(/-/g, '');
      const fname = `amplitude_import_${cd}.sql`;
      downloadBlob(res.data, fname);
    } catch (err) {
      setMessage({ type: 'error', text: `Erreur téléchargement SQL : ${err.response?.data?.detail || err.message}` });
    } finally {
      setDlLoading(false);
    }
  };

  const handleImport = async () => {
    if (!csvFile) return;
    setImportLoading(true);
    setMessage(null);
    setRun(null);
    try {
      const res = await reportingApi.importCsv(csvFile, form.closingDate, form.previousClosingDate, form.skipAutoCorrections || false);
      const r = res.data;
      setRun(r);
      setMessage({
        type: 'info',
        text: `Import lancé (run #${r.id}). L'insertion est en cours en arrière-plan.`,
      });
      setCsvFile(null);
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      setMessage({ type: 'error', text: `Erreur lors de l'import : ${detail}` });
    } finally {
      setImportLoading(false);
    }
  };

  const handleImportTransfert = async () => {
    if (!transfertFile || !form.closingDate) return;
    setTransfertLoading(true);
    setTransfertMessage(null);
    try {
      const res = await reportingApi.importTransfertCsv(transfertFile, form.closingDate);
      const { rows_imported } = res.data;
      setTransfertMessage({
        type: 'success',
        text: `Transferts importés : ${rows_imported} ligne(s) chargée(s) pour l'arrêté du ${form.closingDate}. F1411/F1412 sont maintenant disponibles.`,
      });
      setTransfertFile(null);
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      setTransfertMessage({ type: 'error', text: `Erreur import transferts : ${detail}` });
    } finally {
      setTransfertLoading(false);
    }
  };

  const handleImportBktau = async () => {
    if (!bktauFile || !form.closingDate) return;
    setBktauLoading(true);
    setBktauMessage(null);
    try {
      const res = await reportingApi.importBktauCsv(bktauFile, form.closingDate);
      const { rates_imported, rows_updated } = res.data;
      setBktauMessage({
        type: 'success',
        text: `Taux importés : ${rates_imported} devise(s) chargée(s). ${rows_updated} ligne(s) sm_soldattribut mises à jour pour l'arrêté du ${form.closingDate}.`,
      });
      setBktauFile(null);
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      setBktauMessage({ type: 'error', text: `Erreur import taux : ${detail}` });
    } finally {
      setBktauLoading(false);
    }
  };

  const runStatusMsg = run && (
    run.status === 'completed'
      ? { type: 'success', text: `Import terminé — ${run.rows_loaded} lignes chargées pour l'arrêté du ${formatDisplayDate(run.closing_date)}. Export ZIP et CID disponibles.` }
      : run.status === 'precomputing'
      ? { type: 'info', text: `Import CSV terminé (${run.rows_loaded} lignes). Génération des fichiers COBAC en cours… Cette étape peut prendre 15–30 minutes. Le bouton Export ZIP sera disponible à la fin.` }
      : run.status === 'failed'
      ? { type: 'error', text: `Import échoué : ${run.error_message || 'erreur inconnue.'}` }
      : null
  );

  return (
    <div className="max-w-3xl mx-auto px-6 py-6 space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900 mb-1">Import manuel Amplitude</h1>
        <p className="text-sm text-slate-500">
          Alternative à la connexion Oracle directe — exécutez la requête sur une machine ayant accès à Amplitude, puis uploadez le CSV résultant.
        </p>
      </div>

      <div className="bg-blue-50 border border-blue-200 p-4 text-sm text-blue-800 space-y-1">
        <p className="font-semibold">Procédure</p>
        <ol className="list-decimal list-inside space-y-1">
          <li>Renseignez les deux dates ci-dessous et téléchargez la requête SQL.</li>
          <li>Ouvrez le fichier dans SQL Developer sur la machine accédant à Amplitude et exécutez-le.</li>
          <li>Exportez le résultat en <strong>CSV</strong> (File → Export → Format: CSV, encodage UTF-8).</li>
          <li>Uploadez le fichier CSV dans la section ci-dessous (Étape 2).</li>
          <li><strong>Optionnel — F1411/F1412 :</strong> exécutez aussi <code className="bg-blue-100 px-1">transfert_import.sql</code> (disponible dans le même répertoire), exportez en CSV et uploadez-le dans l&apos;Étape 3.</li>
          <li><strong>Optionnel — Taux de change :</strong> si les taux sont manquants (champ 2 F1514 vide), exportez la table <code className="bg-blue-100 px-1">bktau</code> et uploadez-la dans l&apos;Étape 4.</li>
        </ol>
      </div>

      {message && <Alert type={message.type}>{message.text}</Alert>}
      {runStatusMsg && <Alert type={runStatusMsg.type}>{runStatusMsg.text}</Alert>}

      {run && (run.status === 'running' || run.status === 'pending' || run.status === 'precomputing') && (
        <div className="flex items-center gap-3 text-sm text-slate-600 bg-white border border-slate-200 px-4 py-3">
          <svg className="animate-spin h-4 w-4 text-[#E20613]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <span>Run #{run.id} en cours ({run.status})…</span>
        </div>
      )}

      {/* ── Étape 1 : Télécharger le SQL ── */}
      <div className="bg-white border border-slate-200 shadow-sm">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-2">
          <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#E20613] text-white text-xs font-bold">1</span>
          <h2 className="text-base font-semibold text-slate-800">Télécharger la requête SQL</h2>
        </div>
        <div className="p-6 space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <label className="block space-y-1.5">
              <span className="text-sm font-medium text-slate-700">Date d&apos;arrêté <span className="text-red-500">*</span></span>
              <input
                type="date"
                value={form.closingDate}
                onChange={(e) => setForm((p) => ({ ...p, closingDate: e.target.value }))}
                className="w-full border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:border-[#E20613]"
              />
            </label>
            <label className="block space-y-1.5">
              <span className="text-sm font-medium text-slate-700">Date d&apos;arrêté précédent <span className="text-red-500">*</span></span>
              <input
                type="date"
                value={form.previousClosingDate}
                onChange={(e) => setForm((p) => ({ ...p, previousClosingDate: e.target.value }))}
                className="w-full border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:border-[#E20613]"
              />
            </label>
          </div>
          <button
            type="button"
            disabled={!canDownload || dlLoading}
            onClick={handleDownloadSql}
            className="px-5 py-2 bg-slate-800 text-white text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed hover:bg-slate-700 flex items-center gap-2"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            {dlLoading ? 'Génération…' : 'Télécharger amplitude_import.sql'}
          </button>
          <p className="text-xs text-slate-400">
            Les paramètres <code className="bg-slate-100 px-1">:closing_date</code> et <code className="bg-slate-100 px-1">:previous_closing_date</code> sont déjà remplacés par les dates saisies.
          </p>
        </div>
      </div>

      {/* ── Étape 2 : Uploader le CSV ── */}
      <div className="bg-white border border-slate-200 shadow-sm">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-2">
          <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#E20613] text-white text-xs font-bold">2</span>
          <h2 className="text-base font-semibold text-slate-800">Uploader le CSV résultant</h2>
        </div>
        <div className="p-6 space-y-4">
          <label className="block space-y-1.5">
            <span className="text-sm font-medium text-slate-700">Fichier CSV <span className="text-red-500">*</span></span>
            <input
              type="file"
              accept=".csv"
              onChange={(e) => setCsvFile(e.target.files?.[0] || null)}
              className="block w-full text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:border-0 file:text-sm file:font-medium file:bg-slate-100 file:text-slate-700 hover:file:bg-slate-200"
            />
            {csvFile && <p className="text-xs text-slate-400">{csvFile.name} — {(csvFile.size / 1024).toFixed(0)} Ko</p>}
          </label>
          <p className="text-xs text-slate-400">
            Séparateur virgule ou point-virgule accepté. Encodage UTF-8 ou Latin-1.
            Les dates de type <code className="bg-slate-100 px-1">DD/MM/YY</code> ou <code className="bg-slate-100 px-1">DD-MON-YY</code> sont reconnues automatiquement.
          </p>
          {csvFile && !canDownload && (
            <p className="text-sm text-amber-600 bg-amber-50 border border-amber-200 px-3 py-2">
              ⚠️ Renseignez les dates d&apos;arrêté dans l&apos;étape 1 pour activer l&apos;import.
            </p>
          )}
          <button
            type="button"
            disabled={!canImport || importLoading}
            onClick={handleImport}
            className="px-5 py-2 bg-[#E20613] text-white text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[#c90511] flex items-center gap-2"
          >
            {importLoading && (
              <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            )}
            {importLoading ? 'Import en cours…' : 'Lancer l\'import'}
          </button>
        </div>
      </div>

      {/* ── Étape 3 : Transferts CSV (optionnel) ── */}
      <div className="bg-white border border-slate-200 shadow-sm">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-3">
          <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-slate-400 text-white text-xs font-bold">3</span>
          <div>
            <h2 className="text-base font-semibold text-slate-800">Importer les transferts — F1411/F1412 <span className="text-xs font-normal text-slate-400 ml-1">(optionnel)</span></h2>
            <p className="text-xs text-slate-400 mt-0.5">CSV exporté depuis <code className="bg-slate-100 px-1">transfert_import.sql</code> — colonnes : CODE_MOTIF, MOTIF, PAYS_DESTINATION, TYPE_TRANSF, NOMBRE_TRANSFERT</p>
          </div>
        </div>
        <div className="p-6 space-y-4">
          {transfertMessage && <Alert type={transfertMessage.type}>{transfertMessage.text}</Alert>}
          <label className="block space-y-1.5">
            <span className="text-sm font-medium text-slate-700">Fichier CSV transferts</span>
            <input
              type="file"
              accept=".csv"
              onChange={(e) => setTransfertFile(e.target.files?.[0] || null)}
              className="block w-full text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:border-0 file:text-sm file:font-medium file:bg-slate-100 file:text-slate-700 hover:file:bg-slate-200"
            />
            {transfertFile && <p className="text-xs text-slate-400">{transfertFile.name} — {(transfertFile.size / 1024).toFixed(0)} Ko</p>}
          </label>
          {transfertFile && !form.closingDate && (
            <p className="text-sm text-amber-600 bg-amber-50 border border-amber-200 px-3 py-2">
              ⚠️ Renseignez la date d&apos;arrêté dans l&apos;étape 1 pour activer l&apos;import.
            </p>
          )}
          <button
            type="button"
            disabled={!transfertFile || !form.closingDate || transfertLoading}
            onClick={handleImportTransfert}
            className="px-5 py-2 bg-slate-700 text-white text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed hover:bg-slate-600 flex items-center gap-2"
          >
            {transfertLoading && (
              <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            )}
            {transfertLoading ? 'Import en cours…' : 'Importer les transferts'}
          </button>
        </div>
      </div>

      {/* ── Étape 4 : Taux de change bktau (optionnel) ── */}
      <div className="bg-white border border-slate-200 shadow-sm">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-3">
          <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-slate-400 text-white text-xs font-bold">4</span>
          <div>
            <h2 className="text-base font-semibold text-slate-800">Importer les taux de change (bktau) <span className="text-xs font-normal text-slate-400 ml-1">(optionnel)</span></h2>
            <p className="text-xs text-slate-400 mt-0.5">CSV exporté depuis la table <code className="bg-slate-100 px-1">bktau</code> — colonnes : AGE, DCO, DEV, TAC, TVE, TIND, COURIND</p>
          </div>
        </div>
        <div className="p-6 space-y-4">
          {bktauMessage && <Alert type={bktauMessage.type}>{bktauMessage.text}</Alert>}
          <label className="block space-y-1.5">
            <span className="text-sm font-medium text-slate-700">Fichier CSV bktau</span>
            <input
              type="file"
              accept=".csv"
              onChange={(e) => setBktauFile(e.target.files?.[0] || null)}
              className="block w-full text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:border-0 file:text-sm file:font-medium file:bg-slate-100 file:text-slate-700 hover:file:bg-slate-200"
            />
            {bktauFile && <p className="text-xs text-slate-400">{bktauFile.name} — {(bktauFile.size / 1024).toFixed(0)} Ko</p>}
          </label>
          {bktauFile && !form.closingDate && (
            <p className="text-sm text-amber-600 bg-amber-50 border border-amber-200 px-3 py-2">
              ⚠️ Renseignez la date d&apos;arrêté dans l&apos;étape 1 pour activer l&apos;import.
            </p>
          )}
          <button
            type="button"
            disabled={!bktauFile || !form.closingDate || bktauLoading}
            onClick={handleImportBktau}
            className="px-5 py-2 bg-slate-700 text-white text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed hover:bg-slate-600 flex items-center gap-2"
          >
            {bktauLoading && (
              <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            )}
            {bktauLoading ? 'Import en cours…' : 'Importer les taux'}
          </button>
        </div>
      </div>

      {/* ── Historique récent ── */}
      {runs.length > 0 && (
        <div className="bg-white border border-slate-200 shadow-sm">
          <div className="px-6 py-4 border-b border-slate-100">
            <h2 className="text-base font-semibold text-slate-800">Productions récentes</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide border-b border-slate-200">
                <tr>
                  <th className="px-5 py-3 text-left font-medium">Run</th>
                  <th className="px-5 py-3 text-left font-medium">Date d&apos;arrêté</th>
                  <th className="px-5 py-3 text-left font-medium">Statut</th>
                  <th className="px-5 py-3 text-left font-medium">Lignes</th>
                  <th className="px-5 py-3 text-left font-medium">Lancé le</th>
                </tr>
              </thead>
              <tbody>
                {runs.map((r) => (
                  <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50">
                    <td className="px-5 py-3 text-sm text-slate-500">#{r.id}</td>
                    <td className="px-5 py-3 text-sm font-medium text-slate-800">{formatDisplayDate(r.closing_date)}</td>
                    <td className="px-5 py-3 text-sm"><StatusBadge status={r.status} /></td>
                    <td className="px-5 py-3 text-sm text-slate-600">{r.rows_loaded ?? '—'}</td>
                    <td className="px-5 py-3 text-sm text-slate-400">{r.started_at ? new Date(r.started_at).toLocaleString('fr-FR') : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};
